import { unzipSync } from "fflate";
import type { PDFDocumentProxy, PDFPageProxy } from "pdfjs-dist";
import {
  getDocument,
  GlobalWorkerOptions,
  version as pdfjsVersion,
} from "pdfjs-dist";
// Parcel 2: import worker script as URL so it gets copied to dist and we get a bundle URL
// @ts-expect-error - ?url is a Parcel asset import
// eslint-disable-next-line import/extensions -- .js required for Parcel ?url asset resolution
import pdfWorkerUrl from "pdfjs-dist/build/pdf.worker.min.js?url";
import {
  DEFAULT_SCANNED_PAGE_MODE,
  DEFAULT_VISION_MODEL,
  ScannedPageMode,
} from "./aiCommon";
import {
  classifyPdfPage,
  isBlankPage,
  isUsableText,
  measureTextQuality,
  countOperatorSignals,
  TextContentItem,
} from "./classifyPdfPage";
import {
  renderPdfPageForAttach,
  renderPdfPageToJpeg,
  transcribePageImage,
} from "./ocrPdfPage";
import {
  buildEctdLeafMapFromEntries,
  normalizeEctdPath,
} from "./parseEctdIndex";

GlobalWorkerOptions.workerSrc =
  pdfWorkerUrl ||
  `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsVersion}/pdf.worker.js`;

export type PageStats = {
  totalPages: number;
  scannedPages: number;
  textPages: number;
};

export type ScannedPageImage = {
  pageNumber: number;
  dataUrl: string;
};

export type ParsedPdf = {
  fileName: string;
  text: string;
  /** eCTD structural key (parent element tag name), e.g. "m3-2-p-5-4-batch-analyses" or "pi-doc". */
  ectdKey: string;
  /** Leaf title from the XML, e.g. "batch-analyses" or "common-spc". */
  ectdTitle: string;
  /** Text of the first PDF page only (used for header-based eCTD matching). */
  firstPageText?: string;
  /** Per-file page classification summary. */
  pageStats?: PageStats;
  /**
   * Rendered JPEG data URLs for scanned body pages when
   * `scannedPageMode === "attach-images"`. First page is always transcribed
   * into text for matching, so it is not duplicated here unless it is also
   * needed as a body image (it is included when attach-images is on).
   */
  scannedPageImages?: ScannedPageImage[];
};

export type OcrProgressPhase = "classify" | "extract";

export type OcrProgressEvent = {
  /** Pass-1 page classification vs pass-2 OCR / text extraction. */
  phase: OcrProgressPhase;
  overallCompleted: number;
  overallTotal: number;
  /** PDF files whose OCR/classification is fully done. */
  filesCompleted: number;
  fileCount: number;
  fileName: string;
  /** 1-based index among PDF entries (not shown in UI; kept for diagnostics). */
  fileIndex: number;
  /** OCR/render units completed for this file. */
  filePageCompleted: number;
  /** OCR/render units planned for this file (0 for text-only). */
  filePageTotal: number;
  label?: string;
};

export type ParseZipPdfsOptions = {
  authToken?: string;
  scannedPageMode?: ScannedPageMode;
  /** Vision model for page transcription (defaults to DEFAULT_VISION_MODEL). */
  visionModel?: string;
  /** Max concurrent vision transcriptions across the whole ZIP. */
  ocrConcurrency?: number;
  /**
   * Optional cap on scanned pages transcribed per file in `transcribe` mode
   * (first page always included). When omitted, every non-blank scanned page
   * is transcribed. Remaining pages (when capped) get a placeholder.
   */
  maxScannedPagesPerFile?: number;
  onOcrProgress?: (event: OcrProgressEvent) => void;
};

const DEFAULT_OCR_CONCURRENCY = 6;

/**
 * Open a PDF from a copy of the bytes. pdf.js transfers `data.buffer` to the
 * worker when the Uint8Array owns its whole ArrayBuffer (as fflate returns),
 * which detaches the original. Callers that open the same entry twice must
 * slice so the unzipped bytes stay usable.
 */
function openPdfDocument(
  pdfBytes: Uint8Array
): ReturnType<typeof getDocument> {
  return getDocument({ data: pdfBytes.slice() });
}

function dataUrlToArrayBuffer(dataUrl: string): ArrayBuffer {
  const BASE64_MARKER = ";base64,";
  const base64Index = dataUrl.indexOf(BASE64_MARKER);
  if (base64Index === -1) {
    // Plain data URL (not base64-encoded)
    const parts = dataUrl.split(",");
    const raw = decodeURIComponent(parts[1] || "");
    const buf = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i += 1) buf[i] = raw.charCodeAt(i);
    return buf.buffer;
  }

  const base64 = dataUrl.substring(base64Index + BASE64_MARKER.length);
  const raw = atob(base64);
  const buf = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) buf[i] = raw.charCodeAt(i);
  return buf.buffer;
}

type ClassifiedPage = {
  pageNumber: number;
  pageClass: "text" | "scanned";
  blank: boolean;
  /**
   * True when the embedded text layer is good enough for header matching,
   * even if the page as a whole is classified "scanned" (image-heavy).
   */
  usableText: boolean;
  textLayer: string;
  page: PDFPageProxy;
};

async function classifyPages(pdf: PDFDocumentProxy): Promise<ClassifiedPage[]> {
  const pageNumbers = Array.from({ length: pdf.numPages }, (_, i) => i + 1);
  return Promise.all(
    pageNumbers.map(async (pageNum) => {
      const page = await pdf.getPage(pageNum);
      const content = await page.getTextContent();
      const items = (content.items || []) as TextContentItem[];
      const textLayer = items
        .filter((item) => item.str != null)
        .map(
          (item) =>
            (typeof item.str === "string" ? item.str : "") +
            (item.hasEOL ? "\n" : "")
        )
        .join("");

      let operatorList: { fnArray?: Array<number | string> } | null = null;
      try {
        operatorList = await page.getOperatorList();
      } catch (_) {
        operatorList = null;
      }

      const pageClass = classifyPdfPage(items, operatorList);
      const quality = measureTextQuality(items);
      const blank = isBlankPage(quality, countOperatorSignals(operatorList));
      return {
        pageNumber: pageNum,
        pageClass,
        blank,
        usableText: isUsableText(quality),
        textLayer,
        page,
      };
    })
  );
}

type OcrWorkPlan = {
  filePageTotal: number;
  needFirstTranscribe: boolean;
  bodyScannedCount: number;
  attachCount: number;
};

/**
 * How many OCR/render units a classified PDF will produce for the given mode.
 * Must stay in sync with the work scheduled in `runOcrOnClassified`.
 */
export function planOcrWork(
  classified: Array<{
    pageNumber: number;
    pageClass: "text" | "scanned";
    blank: boolean;
  }>,
  mode: ScannedPageMode,
  maxScannedPagesPerFile?: number
): OcrWorkPlan {
  const scannedNonBlank = classified.filter(
    (p) => p.pageClass === "scanned" && !p.blank
  );
  const first = classified[0];
  const needFirstTranscribe =
    !!first && first.pageClass === "scanned" && !first.blank;

  let bodyScannedCount = scannedNonBlank.filter(
    (p) => p.pageNumber !== 1
  ).length;
  if (
    mode === "transcribe" &&
    typeof maxScannedPagesPerFile === "number" &&
    Number.isFinite(maxScannedPagesPerFile)
  ) {
    const remainingBudget = Math.max(
      0,
      maxScannedPagesPerFile - (needFirstTranscribe ? 1 : 0)
    );
    bodyScannedCount = Math.min(bodyScannedCount, remainingBudget);
  }

  const transcribeCount =
    (needFirstTranscribe ? 1 : 0) +
    (mode === "transcribe" ? bodyScannedCount : 0);
  const attachCount = mode === "attach-images" ? scannedNonBlank.length : 0;

  return {
    filePageTotal: transcribeCount + attachCount,
    needFirstTranscribe,
    bodyScannedCount,
    attachCount,
  };
}

type OcrQueue = {
  enqueue: <T>(task: () => Promise<T>) => Promise<T>;
};

type OcrProgressTracker = {
  /** Emit the fixed-totals starting event (after classification pass). */
  emitReady: (label?: string) => void;
  /** Mark one OCR/render unit done for the given file. */
  bumpPage: (info: {
    fileName: string;
    fileIndex: number;
    filePageTotal: number;
    label: string;
  }) => void;
  /**
   * Mark a text-only (or already-counted) file as finished. No-op if the file
   * was already counted during the classification pass.
   */
  finishFile: (info: { fileName: string; fileIndex: number }) => void;
};

function emitClassifyProgress(
  onOcrProgress: ((event: OcrProgressEvent) => void) | undefined,
  partial: {
    filesCompleted: number;
    fileCount: number;
    fileName?: string;
    fileIndex?: number;
    label?: string;
  }
): void {
  if (typeof onOcrProgress !== "function") return;
  onOcrProgress({
    phase: "classify",
    overallCompleted: 0,
    overallTotal: 0,
    filesCompleted: partial.filesCompleted,
    fileCount: partial.fileCount,
    fileName: partial.fileName || "",
    fileIndex: partial.fileIndex ?? 0,
    filePageCompleted: 0,
    filePageTotal: 0,
    label: partial.label,
  });
}

function createConcurrencyLimiter(limit: number): OcrQueue {
  let active = 0;
  const waiting: Array<() => void> = [];

  const pump = (): void => {
    while (active < limit && waiting.length > 0) {
      const next = waiting.shift();
      if (next) next();
    }
  };

  return {
    enqueue: <T>(task: () => Promise<T>): Promise<T> =>
      new Promise<T>((resolve, reject) => {
        const run = (): void => {
          active += 1;
          task()
            .then(resolve, reject)
            .finally(() => {
              active -= 1;
              pump();
            });
        };
        if (active < limit) run();
        else waiting.push(run);
      }),
  };
}

function createOcrProgressTracker(
  onOcrProgress: ((event: OcrProgressEvent) => void) | undefined,
  totals: {
    overallTotal: number;
    fileCount: number;
    /** Text-only (and failed-plan) files already counted as done. */
    preCompletedFiles: string[];
  }
): OcrProgressTracker {
  let overallCompleted = 0;
  const { overallTotal, fileCount } = totals;
  const finishedFiles = new Set<string>(totals.preCompletedFiles);
  let filesCompleted = finishedFiles.size;
  const filePageDone = new Map<string, number>();

  const emit = (partial: {
    fileName: string;
    fileIndex: number;
    filePageCompleted: number;
    filePageTotal: number;
    label?: string;
  }): void => {
    if (typeof onOcrProgress !== "function") return;
    onOcrProgress({
      phase: "extract",
      overallCompleted,
      overallTotal,
      filesCompleted,
      fileCount,
      fileName: partial.fileName,
      fileIndex: partial.fileIndex,
      filePageCompleted: partial.filePageCompleted,
      filePageTotal: partial.filePageTotal,
      label: partial.label,
    });
  };

  const markFileFinished = (fileName: string): void => {
    if (finishedFiles.has(fileName)) return;
    finishedFiles.add(fileName);
    filesCompleted += 1;
  };

  return {
    emitReady(label?: string): void {
      emit({
        fileName: "",
        fileIndex: 0,
        filePageCompleted: 0,
        filePageTotal: 0,
        label: label || "Counting pages…",
      });
    },
    bumpPage({ fileName, fileIndex, filePageTotal, label }): void {
      overallCompleted += 1;
      const done = (filePageDone.get(fileName) || 0) + 1;
      filePageDone.set(fileName, done);
      if (filePageTotal > 0 && done >= filePageTotal) {
        markFileFinished(fileName);
      }
      emit({
        fileName,
        fileIndex,
        filePageCompleted: done,
        filePageTotal,
        label,
      });
    },
    finishFile({ fileName, fileIndex }): void {
      markFileFinished(fileName);
      emit({
        fileName,
        fileIndex,
        filePageCompleted: 0,
        filePageTotal: 0,
        label: "Text only",
      });
    },
  };
}

/**
 * Open a PDF, classify pages, return the planned OCR unit count. Closes the
 * document so pass-2 OCR reopens fresh (avoids holding every page in memory).
 */
async function planPdfOcrUnits(
  pdfBytes: Uint8Array,
  options: ParseZipPdfsOptions
): Promise<number> {
  const mode = options.scannedPageMode || DEFAULT_SCANNED_PAGE_MODE;
  const maxScanned = options.maxScannedPagesPerFile;
  const loadingTask = openPdfDocument(pdfBytes);
  const pdf: PDFDocumentProxy = await loadingTask.promise;
  try {
    const classified = await classifyPages(pdf);
    return planOcrWork(classified, mode, maxScanned).filePageTotal;
  } finally {
    try {
      if (typeof pdf.destroy === "function") {
        await pdf.destroy();
      }
    } catch (_) {
      // ignore cleanup errors
    }
  }
}

async function extractPdfWithOcr(
  pdfBytes: Uint8Array,
  fileName: string,
  options: ParseZipPdfsOptions,
  queue: OcrQueue,
  progress: OcrProgressTracker,
  ctx: { fileIndex: number; fileCount: number; plannedUnits: number }
): Promise<ParsedPdf> {
  const mode = options.scannedPageMode || DEFAULT_SCANNED_PAGE_MODE;
  const maxScanned = options.maxScannedPagesPerFile;
  const visionOpts = {
    authToken: options.authToken,
    model: options.visionModel || DEFAULT_VISION_MODEL,
  };
  const { fileIndex } = ctx;

  const loadingTask = openPdfDocument(pdfBytes);
  const pdf: PDFDocumentProxy = await loadingTask.promise;

  try {
    const classified = await classifyPages(pdf);
    const pageStats: PageStats = {
      totalPages: classified.length,
      scannedPages: classified.filter((p) => p.pageClass === "scanned").length,
      textPages: classified.filter((p) => p.pageClass === "text").length,
    };

    const plan = planOcrWork(classified, mode, maxScanned);
    const { filePageTotal } = plan;

    const scannedNonBlank = classified.filter(
      (p) => p.pageClass === "scanned" && !p.blank
    );
    const first = classified[0];

    const pagesToTranscribe: ClassifiedPage[] = [];
    if (plan.needFirstTranscribe && first) {
      pagesToTranscribe.push(first);
    }
    if (mode === "transcribe") {
      const body = scannedNonBlank
        .filter((p) => p.pageNumber !== 1)
        .slice(0, plan.bodyScannedCount);
      pagesToTranscribe.push(...body);
    }

    const transcribeByNum = new Map<number, ClassifiedPage>();
    pagesToTranscribe.forEach((p) => {
      transcribeByNum.set(p.pageNumber, p);
    });

    const pagesToAttach = mode === "attach-images" ? scannedNonBlank : [];

    if (filePageTotal === 0) {
      progress.finishFile({ fileName, fileIndex });
    }

    const transcribed = new Map<number, string>();
    await Promise.all(
      Array.from(transcribeByNum.values()).map((p) =>
        queue.enqueue(async () => {
          try {
            const dataUrl = await renderPdfPageToJpeg(p.page);
            if (dataUrl) {
              const text = await transcribePageImage(dataUrl, visionOpts);
              transcribed.set(p.pageNumber, text);
            } else {
              transcribed.set(p.pageNumber, "");
            }
          } catch (_) {
            transcribed.set(p.pageNumber, "");
          } finally {
            progress.bumpPage({
              fileName,
              fileIndex,
              filePageTotal,
              label: `OCR page ${p.pageNumber} of ${classified.length}`,
            });
          }
        })
      )
    );

    const scannedPageImages: ScannedPageImage[] = [];
    if (mode === "attach-images") {
      await Promise.all(
        pagesToAttach.map((p) =>
          queue.enqueue(async () => {
            try {
              const dataUrl = await renderPdfPageForAttach(p.page);
              if (dataUrl) {
                scannedPageImages.push({
                  pageNumber: p.pageNumber,
                  dataUrl,
                });
              }
            } catch (_) {
              // skip failed renders
            } finally {
              progress.bumpPage({
                fileName,
                fileIndex,
                filePageTotal,
                label: `Render page ${p.pageNumber} of ${classified.length}`,
              });
            }
          })
        )
      );
      scannedPageImages.sort((a, b) => a.pageNumber - b.pageNumber);
    }

    const pageTexts: string[] = classified.map((p) => {
      if (p.pageClass === "text") return p.textLayer;
      if (p.blank) return "";
      if (transcribed.has(p.pageNumber)) {
        return transcribed.get(p.pageNumber) || "";
      }
      if (mode === "attach-images") {
        return `[Page ${p.pageNumber}: see attached image "${fileName} p.${p.pageNumber}"]`;
      }
      return `[further scanned pages omitted]`;
    });

    let sawOmitted = false;
    const cleaned = pageTexts.map((t) => {
      if (t === "[further scanned pages omitted]") {
        if (sawOmitted) return "";
        sawOmitted = true;
        return t;
      }
      return t;
    });

    const text = cleaned.filter((t) => t.length > 0).join("\n\n");
    // Header matching needs exact line structure (section codes at line
    // start). The embedded text layer is exact; a vision transcription is
    // approximate (markdown, merged lines), so prefer the text layer whenever
    // it is usable, even when the page was classified scanned.
    const firstPageText =
      (first &&
        (first.pageClass === "text" || first.usableText
          ? first.textLayer
          : transcribed.get(1) || first.textLayer)) ||
      "";

    return {
      fileName,
      text,
      ectdKey: "",
      ectdTitle: "",
      firstPageText,
      pageStats,
      ...(scannedPageImages.length > 0 ? { scannedPageImages } : {}),
    };
  } finally {
    // pdfjs handles cleanup of workers automatically when tasks resolve
  }
}

/** Lightweight text-only extraction (no OCR) — used as a fallback. */
async function extractPdfPages(pdfBytes: Uint8Array): Promise<string[]> {
  const loadingTask = openPdfDocument(pdfBytes);
  const pdf: PDFDocumentProxy = await loadingTask.promise;
  try {
    const classified = await classifyPages(pdf);
    return classified.map((p) => p.textLayer);
  } finally {
    // cleanup handled by pdfjs
  }
}

export async function parseZipPdfsFromArrayBuffer(
  zipData: ArrayBuffer,
  options: ParseZipPdfsOptions = {}
): Promise<ParsedPdf[]> {
  const bytes = new Uint8Array(zipData);
  const entries = unzipSync(bytes); // Record<fileName, Uint8Array>

  const ectdLeafMap = buildEctdLeafMapFromEntries(entries);

  const pdfEntries = Object.entries(entries).filter(([name]) => {
    const normalized = name.replace(/\\/g, "/");
    const baseName = normalized.split("/").pop() || "";
    return (
      normalized.toLowerCase().endsWith(".pdf") &&
      // Skip macOS ZIP artifacts (AppleDouble resource forks)
      !normalized.includes("__MACOSX") &&
      !baseName.startsWith("._")
    );
  });

  const fileCount = pdfEntries.length;

  emitClassifyProgress(options.onOcrProgress, {
    filesCompleted: 0,
    fileCount,
    label: fileCount > 0 ? "Classifying PDF files…" : "No PDF files",
  });

  let classifiedCount = 0;

  // Pass 1: classify every PDF (no vision) so overallTotal is fixed up front.
  const plans = await Promise.all(
    pdfEntries.map(async ([name, content], index) => {
      const fileIndex = index + 1;
      const baseName = name.replace(/\\/g, "/").split("/").pop() || name;
      let plan: {
        name: string;
        content: Uint8Array;
        plannedUnits: number;
        ok: boolean;
      };
      try {
        const plannedUnits = await planPdfOcrUnits(content, options);
        plan = { name, content, plannedUnits, ok: true };
      } catch (_) {
        plan = { name, content, plannedUnits: 0, ok: false };
      }
      classifiedCount += 1;
      emitClassifyProgress(options.onOcrProgress, {
        filesCompleted: classifiedCount,
        fileCount,
        fileName: name,
        fileIndex,
        label: `Classifying ${baseName}`,
      });
      return plan;
    })
  );

  const overallTotal = plans.reduce((sum, p) => sum + p.plannedUnits, 0);
  const preCompletedFiles = plans
    .filter((p) => p.plannedUnits === 0)
    .map((p) => p.name);

  const concurrency = options.ocrConcurrency ?? DEFAULT_OCR_CONCURRENCY;
  const queue = createConcurrencyLimiter(concurrency);
  const progress = createOcrProgressTracker(options.onOcrProgress, {
    overallTotal,
    fileCount,
    preCompletedFiles,
  });
  progress.emitReady(
    overallTotal > 0
      ? `Ready: ${overallTotal} OCR pages across ${fileCount} files`
      : `Ready: ${fileCount} text-only files`
  );

  type SettledResult =
    | { status: "fulfilled"; value: ParsedPdf }
    | { status: "rejected"; reason: unknown };

  // Pass 2: OCR / extract with fixed progress denominators.
  const settled: SettledResult[] = await Promise.all(
    plans.map(async (plan, index): Promise<SettledResult> => {
      const fileIndex = index + 1;
      try {
        const parsed = await extractPdfWithOcr(
          plan.content,
          plan.name,
          options,
          queue,
          progress,
          {
            fileIndex,
            fileCount,
            plannedUnits: plan.plannedUnits,
          }
        );
        const normalizedName = normalizeEctdPath(plan.name);
        const leafInfo = ectdLeafMap[normalizedName];
        return {
          status: "fulfilled",
          value: {
            ...parsed,
            fileName: plan.name,
            ectdKey: leafInfo?.parentName || "",
            ectdTitle: leafInfo?.title || "",
          },
        };
      } catch (reason) {
        try {
          progress.finishFile({
            fileName: plan.name,
            fileIndex,
          });
          const pageTexts = await extractPdfPages(plan.content);
          const normalizedName = normalizeEctdPath(plan.name);
          const leafInfo = ectdLeafMap[normalizedName];
          return {
            status: "fulfilled",
            value: {
              fileName: plan.name,
              text: pageTexts.join("\n\n"),
              ectdKey: leafInfo?.parentName || "",
              ectdTitle: leafInfo?.title || "",
              firstPageText: pageTexts[0] || "",
              pageStats: {
                totalPages: pageTexts.length,
                scannedPages: 0,
                textPages: pageTexts.length,
              },
            },
          };
        } catch (fallbackReason) {
          return { status: "rejected", reason: fallbackReason || reason };
        }
      }
    })
  );

  const fulfilled = settled.filter(
    (s): s is { status: "fulfilled"; value: ParsedPdf } =>
      s.status === "fulfilled"
  );
  if (fileCount > 0 && fulfilled.length === 0) {
    const firstReject = settled.find(
      (s): s is { status: "rejected"; reason: unknown } =>
        s.status === "rejected"
    );
    const reason = firstReject?.reason;
    let message = "All PDF files failed to parse";
    if (reason instanceof Error) {
      message = reason.message;
    } else if (reason) {
      message = String(reason);
    }
    throw new Error(message);
  }

  return fulfilled.map((s) => s.value);
}

export async function parseZipPdfsFromFile(
  file: File,
  options: ParseZipPdfsOptions = {}
): Promise<ParsedPdf[]> {
  const arrayBuffer = await file.arrayBuffer();
  return parseZipPdfsFromArrayBuffer(arrayBuffer, options);
}

export async function parseZipPdfsFromDataUrl(
  dataUrl: string,
  options: ParseZipPdfsOptions = {}
): Promise<ParsedPdf[]> {
  const buffer = dataUrlToArrayBuffer(dataUrl);
  return parseZipPdfsFromArrayBuffer(buffer, options);
}

/**
 * Parse a single PDF from a data URL (e.g. from file input). Returns one ParsedPdf
 * with empty ectdKey/ectdTitle; caller should set these from the selected reference.
 */
export async function parseSinglePdfFromDataUrl(
  dataUrl: string,
  fileName: string,
  options: ParseZipPdfsOptions = {}
): Promise<ParsedPdf> {
  const buffer = dataUrlToArrayBuffer(dataUrl);
  const bytes = new Uint8Array(buffer);

  let plannedUnits = 0;
  try {
    plannedUnits = await planPdfOcrUnits(bytes, options);
  } catch (_) {
    plannedUnits = 0;
  }

  const concurrency = options.ocrConcurrency ?? DEFAULT_OCR_CONCURRENCY;
  const queue = createConcurrencyLimiter(concurrency);
  const progress = createOcrProgressTracker(options.onOcrProgress, {
    overallTotal: plannedUnits,
    fileCount: 1,
    preCompletedFiles: plannedUnits === 0 ? [fileName] : [],
  });
  progress.emitReady(
    plannedUnits > 0
      ? `Ready: ${plannedUnits} OCR pages`
      : "Ready: text-only file"
  );

  return extractPdfWithOcr(bytes, fileName, options, queue, progress, {
    fileIndex: 1,
    fileCount: 1,
    plannedUnits,
  });
}
